import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import styles from './FileUpload.module.css';
import Modal from 'react-modal';
import GoToDashboardButton from '../components/GoToDashboardButton';
import { buildApiUrl, fetchCsrfToken } from '../api/client';
import { describeRequestError, summarizeUploadResults } from '../utils/userFacingErrors';

const FileUpload = () => {
    const { t } = useTranslation();
    const [files, setFiles] = useState([]);
    const [uploadStatus, setUploadStatus] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [activeFileName, setActiveFileName] = useState('');
    const [processedFiles, setProcessedFiles] = useState(0);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [duplicateCount, setDuplicateCount] = useState(0);

    const fileInputRef = useRef(null);

    const openHelp = () => setIsHelpOpen(true);
    const closeHelp = () => setIsHelpOpen(false);

    const handleBrowseClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        setFiles(Array.from(e.target.files));
        setUploadStatus(null);
        setUploadProgress(0);
        setActiveFileName('');
        setProcessedFiles(0);
        setDuplicateCount(0);
    };

    const handleDeleteFile = (index) => {
        if (isUploading) return;
        setFiles(files.filter((_, i) => i !== index));
    };

    const updateFileProgress = (completedFiles, fileFraction, totalFiles) => {
        const totalProgress = ((completedFiles + fileFraction) / totalFiles) * 100;
        setUploadProgress(Math.min(99, Math.max(1, Math.round(totalProgress))));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!files.length || isUploading) return;

        const queuedFiles = [...files];
        const totalFiles = queuedFiles.length;
        const responses = [];
        let duplicateFiles = 0;

        try {
            setIsUploading(true);
            setUploadStatus(null);
            setDuplicateCount(0);
            setProcessedFiles(0);
            setUploadProgress(1);

            const csrfToken = await fetchCsrfToken();

            for (let index = 0; index < queuedFiles.length; index += 1) {
                const file = queuedFiles[index];
                const formData = new FormData();
                formData.append('files', file);
                setActiveFileName(file.name);
                setProcessedFiles(index);

                const response = await axios.post(buildApiUrl('/api/upload'), formData, {
                    headers: { 'Content-Type': 'multipart/form-data', 'X-CSRF-Token': csrfToken },
                    withCredentials: true,
                    onUploadProgress: (progressEvent) => {
                        const total = progressEvent.total || file.size || 1;
                        const bytesFraction = Math.min(progressEvent.loaded / total, 1);
                        updateFileProgress(index, bytesFraction * 0.85, totalFiles);
                    },
                });

                updateFileProgress(index, 0.95, totalFiles);
                const responseArray = Array.isArray(response.data) ? response.data : [response.data];
                responses.push(...responseArray);
                setProcessedFiles(index + 1);
                setUploadProgress(Math.round(((index + 1) / totalFiles) * 100));
            }

            const summary = summarizeUploadResults(responses, 'production');
            responses.forEach((fileResponse) => {
                if (fileResponse.message && fileResponse.message.includes('Duplicate filename')) duplicateFiles += 1;
            });

            setDuplicateCount(duplicateFiles);
            setUploadStatus(summary);
            setFiles([]);
            setUploadProgress(100);

        } catch (err) {
            const details = describeRequestError(err, t('Upload failed. Please try again.'));
            setUploadStatus({ success: false, ...details });
            setUploadProgress(0);
        } finally {
            setIsUploading(false);
            setActiveFileName('');
        }
    };

    return (
        <div className={styles.container}>
            <GoToDashboardButton />

            <div>
                    <header className={styles.pageHeader}>
                        <div>
                            <p className={styles.eyebrow}>{t('Data intake')}</p>
                            <h2 className={styles.title}>{t('Upload Files')}</h2>
                            <p className={styles.description}>{t('Add test result files to populate the dashboard, charts, ML runs, and root-cause tools.')}</p>
                        </div>
                    </header>

                    <div className={styles.uploadBox}>

                        <div className={styles.dropArea}>
                            <button
                                type="button"
                                onClick={handleBrowseClick}
                                className={styles.uploadButton}
                            >
                                {t('Browse Files')}
                            </button>

                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleFileChange}
                                className={styles.fileInput}
                                style={{ display: 'none' }}
                            />

                            <p className={styles.fileLimit}>
                                {t('Individual file size limit is 10MB')}
                            </p>
                        </div>

                        {duplicateCount > 0 && (
                            <div className={styles.duplicateMessage}>
                                {t('{{count}} duplicate file(s) found. But don\'t worry, we\'ll handle it for you.', { count: duplicateCount })}
                            </div>
                        )}

                        <div className={styles.fileListSection}>

                            {files.length > 0 && (
                                <div className={styles.actionButtons}>
                                    <button
                                        onClick={handleSubmit}
                                        className={styles.uploadButton}
                                        disabled={isUploading}
                                    >
                                        {isUploading ? t('Uploading...') : t('Upload')}
                                    </button>

                                    <button
                                        onClick={() => setFiles([])}
                                        className={styles.cancelButton}
                                        disabled={isUploading}
                                    >
                                        {t('Cancel')}
                                    </button>
                                </div>
                            )}

                            {(uploadProgress > 0 || isUploading) && (
                                <div className={styles.progressPanel}>
                                    <div className={styles.progressMeta}>
                                        <span>
                                            {isUploading
                                                ? `Processing file ${Math.min(processedFiles + 1, files.length)} of ${files.length}`
                                                : uploadProgress === 100
                                                    ? t('Upload complete')
                                                    : t('Upload progress')}
                                        </span>
                                        <strong>{uploadProgress}%</strong>
                                    </div>
                                    <div className={styles.progressBarContainer}>
                                        <div className={styles.progressTrackGlow} style={{ width: `${uploadProgress}%` }} />
                                        <div
                                            className={`${styles.progressBar} ${isUploading ? styles.progressBarActive : ''}`}
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                    <div className={styles.progressDetail}>
                                        {activeFileName || t('{{processed}} of {{total}} file(s) completed', { processed: processedFiles, total: files.length })}
                                    </div>
                                </div>
                            )}

                            <h3 className={styles.fileListTitle}>{t('Upload Queue')}</h3>

                            <ul className={styles.fileList}>
                                {files.map((file, index) => (
                                    <li key={index} className={styles.fileItem}>
                                        <div className={styles.fileInfo}>
                                            <span className={styles.fileType}>
                                                {file.name.split('.').pop().toUpperCase()}
                                            </span>

                                            <span className={styles.fileName}>
                                                {file.name}
                                            </span>

                                            <span className={styles.fileSize}>
                                                {(file.size / 1024).toFixed(2)} KB
                                            </span>
                                        </div>

                                        <button
                                            onClick={() => handleDeleteFile(index)}
                                            className={styles.deleteButton}
                                        >
                                            🗑️
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {uploadStatus && (
                            <div className={uploadStatus.success ? styles.successMessage : styles.errorMessage}>
                                <strong>{uploadStatus.title || uploadStatus.message}</strong>
                                {uploadStatus.title ? <p>{uploadStatus.message}</p> : null}
                                {uploadStatus.details ? <p className={styles.errorDetail}>{uploadStatus.details}</p> : null}
                                {uploadStatus.actions?.length ? (
                                    <ul className={styles.errorActionList}>
                                        {uploadStatus.actions.map((action) => <li key={action}>{action}</li>)}
                                    </ul>
                                ) : null}
                                {uploadStatus.results?.length ? (
                                    <div className={styles.fileResultList}>
                                        {uploadStatus.results.map((result) => (
                                            <div key={`${result.filename}-${result.status}`} className={styles.fileResultItem}>
                                                <span className={`${styles.fileResultBadge} ${styles[`fileResult${result.status}`] || ''}`}>{result.status}</span>
                                                <strong>{result.filename}</strong>
                                                <p>{result.message}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>

                    <button
                        className={styles.helpButton}
                        onClick={openHelp}
                        aria-label={t('Help')}
                    >
                        ?
                    </button>

                    <Modal
                        isOpen={isHelpOpen}
                        onRequestClose={closeHelp}
                        className={styles.helpModalContent}
                        overlayClassName={styles.helpModalOverlay}
                        contentLabel={t('Help Information')}
                    >
                        <button
                            onClick={closeHelp}
                            className={styles.closeHelpButton}
                        >
                            {t('Close')}
                        </button>

                        <div className={styles.helpText}>
                            <h2>{t('About this Page')}</h2>

                            <p>
                                {t('This page allows you to upload hardware test data files, which are stored and made available for further analysis and visualization.')}
                            </p>

                            <ul>
                                <li><strong>{t('Browse Files')}:</strong> {t('Click the button to select files from your computer.')}</li>
                                <li><strong>{t('Upload Progress')}:</strong> {t('Displays real-time upload progress.')}</li>
                                <li><strong>{t('Duplicate Detection')}:</strong> {t('Skips files already uploaded.')}</li>
                                <li><strong>{t('Metadata Extraction')}:</strong> {t('Extracts machine ID, serial number, test times, and AteSwVersion.')}</li>
                                <li><strong>{t('Step Data')}:</strong> {t('Parses CSV test step data for analysis and charts.')}</li>
                            </ul>
                        </div>
                    </Modal>

            </div>
        </div>
    );
};

export default FileUpload;
